import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "@/routes";
import { Activity, CreditCard, Camera, Upload, X, Loader2, ScanLine, CheckCircle2, ArrowRight, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { ScanConfirmDialog } from "@/components/intake/ScanConfirmDialog";
import { US_STATES } from "@/components/intake/IntakeFormSchema";

interface UploadState {
  isUploading: boolean;
  preview: string | null;
  url: string | null;
}

interface ExtractedData {
  insurance_type?: string;
  member_name?: string;
  member_id?: string;
  medicare_id?: string;
  medicaid_id?: string;
  group_id?: string;
  plan_type?: string;
  payer_name?: string;
  claims_address?: string;
  payer_phone?: string;
  edi_payer_id?: string;
  rx_bin?: string;
  rx_pcn?: string;
  rx_group?: string;
  rx_id?: string;
  effective_date?: string;
  copay_amount?: string;
  deductible_info?: string;
  network_tier?: string;
  medicare_part_a?: boolean;
  medicare_part_b?: boolean;
  medicare_part_c?: boolean;
  medicare_part_d?: boolean;
}

export default function MemberOnboarding() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState<"upload" | "review">("upload");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Upload states
  const [cardFront, setCardFront] = useState<UploadState>({ isUploading: false, preview: null, url: null });
  const [cardBack, setCardBack] = useState<UploadState>({ isUploading: false, preview: null, url: null });

  // Scan state
  const [isScanning, setIsScanning] = useState(false);
  const [scanData, setScanData] = useState<ExtractedData | null>(null);
  const [showScanConfirm, setShowScanConfirm] = useState(false);

  // Form fields (populated from scan or manual entry)
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    date_of_birth: "",
    phone: "",
    email: user?.email || "",
    address_line1: "",
    city: "",
    state: "",
    zip_code: "",
    insurance_type: "" as string,
    medicare_id: "",
    medicaid_id: "",
    group_number: "",
    plan_type: "",
    payer_name: "",
    claims_address: "",
    payer_phone: "",
    edi_payer_id: "",
    rx_bin: "",
    rx_pcn: "",
    rx_group: "",
    rx_id: "",
    effective_date: "",
    copay_amount: "",
    deductible_info: "",
    network_tier: "",
    medicare_part_a: false,
    medicare_part_b: false,
    medicare_part_c: false,
    medicare_part_d: false,
    emergency_contact_name: "",
    emergency_contact_phone: "",
    emergency_contact_relationship: "",
  });

  const updateField = (field: string, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const uploadFile = async (
    file: File,
    folder: string,
    setState: React.Dispatch<React.SetStateAction<UploadState>>
  ) => {
    if (!user) return null;
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return null;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be less than 5MB");
      return null;
    }

    setState((prev) => ({ ...prev, isUploading: true }));

    try {
      const reader = new FileReader();
      const previewPromise = new Promise<string>((resolve) => {
        reader.onload = (e) => resolve(e.target?.result as string);
      });
      reader.readAsDataURL(file);
      const preview = await previewPromise;
      setState((prev) => ({ ...prev, preview }));

      const fileExt = file.name.split(".").pop();
      const fileName = `${user.id}/${folder}/${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from("intake-documents")
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: signedUrlData } = await supabase.storage
        .from("intake-documents")
        .createSignedUrl(fileName, 60 * 60 * 24 * 365);

      const url = signedUrlData?.signedUrl || null;
      setState((prev) => ({ ...prev, url, isUploading: false }));

      return { preview, url };
    } catch (error: any) {
      console.error("Upload error:", error);
      toast.error(error.message || "Failed to upload photo");
      setState((prev) => ({ ...prev, preview: null, isUploading: false }));
      return null;
    }
  };

  const scanInsuranceCard = async (imageBase64: string) => {
    setIsScanning(true);
    try {
      const { data, error } = await supabase.functions.invoke("scan-insurance-card", {
        body: { imageBase64 },
      });
      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || "Scan failed");

      setScanData(data.data);
      setShowScanConfirm(true);
    } catch (error: any) {
      console.error("Insurance scan error:", error);
      toast.error(error.message || "Failed to scan insurance card. You can enter details manually.");
    } finally {
      setIsScanning(false);
    }
  };

  const applyScanData = () => {
    if (!scanData) return;

    const updates: Record<string, string | boolean> = {};

    if (scanData.insurance_type && ["medicare", "medicaid", "dual_eligible"].includes(scanData.insurance_type)) {
      updates.insurance_type = scanData.insurance_type;
    }
    if (scanData.medicare_id) updates.medicare_id = scanData.medicare_id;
    if (scanData.medicaid_id) updates.medicaid_id = scanData.medicaid_id;
    if (scanData.medicare_part_a !== undefined) updates.medicare_part_a = !!scanData.medicare_part_a;
    if (scanData.medicare_part_b !== undefined) updates.medicare_part_b = !!scanData.medicare_part_b;
    if (scanData.medicare_part_c !== undefined) updates.medicare_part_c = !!scanData.medicare_part_c;
    if (scanData.medicare_part_d !== undefined) updates.medicare_part_d = !!scanData.medicare_part_d;

    const fieldMap: Record<string, string> = {
      group_id: "group_number",
      plan_type: "plan_type",
      payer_name: "payer_name",
      claims_address: "claims_address",
      payer_phone: "payer_phone",
      edi_payer_id: "edi_payer_id",
      rx_bin: "rx_bin",
      rx_pcn: "rx_pcn",
      rx_group: "rx_group",
      rx_id: "rx_id",
      effective_date: "effective_date",
      copay_amount: "copay_amount",
      deductible_info: "deductible_info",
      network_tier: "network_tier",
    };

    for (const [scanKey, formKey] of Object.entries(fieldMap)) {
      if (scanData[scanKey as keyof ExtractedData]) {
        updates[formKey] = scanData[scanKey as keyof ExtractedData] as string;
      }
    }

    if (scanData.member_name) {
      const parts = scanData.member_name.split(" ");
      if (parts.length >= 2 && !formData.first_name) {
        updates.first_name = parts[0];
        updates.last_name = parts.slice(1).join(" ");
      }
    }

    setFormData((prev) => ({ ...prev, ...updates }));
    setShowScanConfirm(false);
    setStep("review");
    toast.success("Insurance data applied! Please review and complete the remaining fields.");
  };

  const discardScanData = () => {
    setScanData(null);
    setShowScanConfirm(false);
    setStep("review");
    toast.info("Scan discarded. Please fill in your information manually.");
  };

  const handleFrontUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const result = await uploadFile(file, "insurance-front", setCardFront);
    if (result?.preview) {
      scanInsuranceCard(result.preview);
    }
  };

  const handleBackUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await uploadFile(file, "insurance-back", setCardBack);
  };

  const handleSubmit = async () => {
    if (!user) return;

    // Basic validation
    if (!formData.first_name || !formData.last_name || !formData.date_of_birth || !formData.phone) {
      toast.error("Please fill in all required fields (name, date of birth, phone).");
      return;
    }
    if (!formData.address_line1 || !formData.city || !formData.state || !formData.zip_code) {
      toast.error("Please fill in your address.");
      return;
    }
    if (!formData.insurance_type) {
      toast.error("Please select an insurance type.");
      return;
    }
    if (!formData.emergency_contact_name || !formData.emergency_contact_phone || !formData.emergency_contact_relationship) {
      toast.error("Please fill in emergency contact information.");
      return;
    }

    setIsSubmitting(true);
    try {
      const { error } = await (supabase as any).from("patients").insert({
        user_id: user.id,
        first_name: formData.first_name,
        last_name: formData.last_name,
        date_of_birth: formData.date_of_birth,
        phone: formData.phone,
        email: formData.email,
        address_line1: formData.address_line1,
        city: formData.city,
        state: formData.state,
        zip_code: formData.zip_code,
        insurance_type: formData.insurance_type,
        medicare_id: formData.medicare_id || null,
        medicaid_id: formData.medicaid_id || null,
        group_number: formData.group_number || null,
        plan_type: formData.plan_type || null,
        payer_name: formData.payer_name || null,
        claims_address: formData.claims_address || null,
        payer_phone: formData.payer_phone || null,
        edi_payer_id: formData.edi_payer_id || null,
        rx_bin: formData.rx_bin || null,
        rx_pcn: formData.rx_pcn || null,
        rx_group: formData.rx_group || null,
        rx_id: formData.rx_id || null,
        effective_date: formData.effective_date || null,
        copay_amount: formData.copay_amount || null,
        deductible_info: formData.deductible_info || null,
        network_tier: formData.network_tier || null,
        medicare_part_a: formData.medicare_part_a,
        medicare_part_b: formData.medicare_part_b,
        medicare_part_c: formData.medicare_part_c,
        medicare_part_d: formData.medicare_part_d,
        emergency_contact_name: formData.emergency_contact_name,
        emergency_contact_phone: formData.emergency_contact_phone,
        emergency_contact_relationship: formData.emergency_contact_relationship,
        insurance_card_front_url: cardFront.url || null,
        insurance_card_back_url: cardBack.url || null,
        consent_given: true,
        signature: formData.first_name + " " + formData.last_name,
        status: "completed",
      });

      if (error) throw error;

      // Sync structured names to profiles
      await supabase.from("profiles").update({
        first_name: formData.first_name,
        last_name: formData.last_name,
        display_name: `${formData.first_name} ${formData.last_name}`.trim(),
      } as any).eq("id", user.id);

      toast.success("Welcome! Your profile has been set up successfully.");
      navigate(ROUTES.MEMBER);
    } catch (error: any) {
      console.error("Submit error:", error);
      toast.error(error.message || "Failed to save your information.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const UploadCard = ({
    title,
    description,
    state,
    onUpload,
    onRemove,
  }: {
    title: string;
    description: string;
    state: UploadState;
    onUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onRemove: () => void;
  }) => (
    <Card className="border-border/50">
      <CardContent className="pt-6">
        <div className="flex items-center gap-2 mb-3">
          <CreditCard className="h-5 w-5 text-primary" />
          <div>
            <h4 className="font-medium text-foreground">{title}</h4>
            <p className="text-xs text-muted-foreground">{description}</p>
          </div>
        </div>
        {state.preview || state.url ? (
          <div className="relative">
            <img src={state.preview || state.url || ""} alt={title} className="w-full h-40 object-cover rounded-lg border" />
            <Button type="button" variant="destructive" size="icon" className="absolute top-2 right-2 h-8 w-8" onClick={onRemove}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <label className="flex flex-col items-center justify-center h-40 border-2 border-dashed border-muted-foreground/25 rounded-lg cursor-pointer hover:border-primary/50 transition-colors">
            {state.isUploading ? (
              <div className="flex flex-col items-center gap-2">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <span className="text-sm text-muted-foreground">Uploading...</span>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <div className="flex gap-2">
                  <Camera className="h-6 w-6 text-muted-foreground" />
                  <Upload className="h-6 w-6 text-muted-foreground" />
                </div>
                <span className="text-sm text-muted-foreground">Take photo or upload</span>
                <span className="text-xs text-muted-foreground">JPG, PNG up to 5MB</span>
              </div>
            )}
            <input type="file" accept="image/*" capture="environment" className="hidden" onChange={onUpload} disabled={state.isUploading} />
          </label>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl gradient-primary shadow-lg">
            <Activity className="h-7 w-7 text-primary-foreground" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-bold text-foreground">Welcome to Vivien Health</h1>
            <p className="text-sm text-muted-foreground">Let's set up your profile</p>
          </div>
        </div>

        {step === "upload" && (
          <Card className="border-border/50 shadow-xl">
            <CardHeader className="text-center">
              <CardTitle className="font-display text-xl flex items-center justify-center gap-2">
                <ScanLine className="h-5 w-5 text-primary" />
                Upload Your Insurance Card
              </CardTitle>
              <CardDescription>
                Upload photos of your insurance card and we'll automatically fill in your information using AI.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {isScanning && (
                <div className="flex items-center justify-center gap-2 p-3 rounded-lg bg-primary/5 border border-primary/20">
                  <Loader2 className="h-4 w-4 animate-spin text-primary" />
                  <span className="text-sm text-primary font-medium">Scanning your insurance card with AI...</span>
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-4">
                <UploadCard
                  title="Front of Card"
                  description="Auto-scans to fill your info"
                  state={cardFront}
                  onUpload={handleFrontUpload}
                  onRemove={() => setCardFront({ isUploading: false, preview: null, url: null })}
                />
                <UploadCard
                  title="Back of Card"
                  description="Back side of insurance card"
                  state={cardBack}
                  onUpload={handleBackUpload}
                  onRemove={() => setCardBack({ isUploading: false, preview: null, url: null })}
                />
              </div>

              <div className="relative my-2">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-border" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-card px-2 text-muted-foreground">or</span>
                </div>
              </div>

              <Button variant="outline" className="w-full" onClick={() => setStep("review")}>
                Skip — I'll enter details manually
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </CardContent>
          </Card>
        )}

        {step === "review" && (
          <Card className="border-border/50 shadow-xl">
            <CardHeader>
              <CardTitle className="font-display text-xl flex items-center gap-2">
                <User className="h-5 w-5 text-primary" />
                Complete Your Profile
              </CardTitle>
              <CardDescription>
                {scanData ? "We've pre-filled what we could from your insurance card. Please review and complete the remaining fields." : "Fill in your personal and insurance information."}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Personal Information */}
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">Personal Information</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="first_name">First Name *</Label>
                    <Input id="first_name" value={formData.first_name} onChange={(e) => updateField("first_name", e.target.value)} required />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="last_name">Last Name *</Label>
                    <Input id="last_name" value={formData.last_name} onChange={(e) => updateField("last_name", e.target.value)} required />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="dob">Date of Birth *</Label>
                    <Input id="dob" type="date" value={formData.date_of_birth} onChange={(e) => updateField("date_of_birth", e.target.value)} required />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="phone">Phone *</Label>
                    <Input id="phone" type="tel" value={formData.phone} onChange={(e) => updateField("phone", e.target.value)} required />
                  </div>
                </div>
                <div className="mt-3 space-y-1.5">
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" value={formData.email} onChange={(e) => updateField("email", e.target.value)} />
                </div>
              </div>

              {/* Address */}
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">Address</h3>
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="address">Street Address *</Label>
                    <Input id="address" value={formData.address_line1} onChange={(e) => updateField("address_line1", e.target.value)} required />
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-1.5">
                      <Label htmlFor="city">City *</Label>
                      <Input id="city" value={formData.city} onChange={(e) => updateField("city", e.target.value)} required />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="state">State *</Label>
                      <Select value={formData.state} onValueChange={(v) => updateField("state", v)}>
                        <SelectTrigger><SelectValue placeholder="State" /></SelectTrigger>
                        <SelectContent>
                          {US_STATES.map((s) => (
                            <SelectItem key={s} value={s}>{s}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="zip">ZIP *</Label>
                      <Input id="zip" value={formData.zip_code} onChange={(e) => updateField("zip_code", e.target.value)} required />
                    </div>
                  </div>
                </div>
              </div>

              {/* Insurance Information */}
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                  Insurance Information
                  {scanData && <Badge variant="secondary" className="ml-2 text-xs"><CheckCircle2 className="h-3 w-3 mr-1" />Auto-filled</Badge>}
                </h3>
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <Label>Insurance Type *</Label>
                    <Select value={formData.insurance_type} onValueChange={(v) => updateField("insurance_type", v)}>
                      <SelectTrigger><SelectValue placeholder="Select insurance type" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="medicare">Medicare</SelectItem>
                        <SelectItem value="medicaid">Medicaid</SelectItem>
                        <SelectItem value="dual_eligible">Dual Eligible</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label>Medicare ID</Label>
                      <Input value={formData.medicare_id} onChange={(e) => updateField("medicare_id", e.target.value)} />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Medicaid ID</Label>
                      <Input value={formData.medicaid_id} onChange={(e) => updateField("medicaid_id", e.target.value)} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label>Group Number</Label>
                      <Input value={formData.group_number} onChange={(e) => updateField("group_number", e.target.value)} />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Plan Type</Label>
                      <Input value={formData.plan_type} onChange={(e) => updateField("plan_type", e.target.value)} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label>Payer Name</Label>
                      <Input value={formData.payer_name} onChange={(e) => updateField("payer_name", e.target.value)} />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Payer Phone</Label>
                      <Input value={formData.payer_phone} onChange={(e) => updateField("payer_phone", e.target.value)} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Emergency Contact */}
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">Emergency Contact</h3>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1.5">
                    <Label>Name *</Label>
                    <Input value={formData.emergency_contact_name} onChange={(e) => updateField("emergency_contact_name", e.target.value)} required />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Phone *</Label>
                    <Input type="tel" value={formData.emergency_contact_phone} onChange={(e) => updateField("emergency_contact_phone", e.target.value)} required />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Relationship *</Label>
                    <Input value={formData.emergency_contact_relationship} onChange={(e) => updateField("emergency_contact_relationship", e.target.value)} required />
                  </div>
                </div>
              </div>

              <Button className="w-full gradient-primary" onClick={handleSubmit} disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    Complete Setup
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        )}

        <ScanConfirmDialog
          open={showScanConfirm}
          data={scanData}
          onConfirm={applyScanData}
          onCancel={discardScanData}
        />
      </div>
    </div>
  );
}
